import styled, { keyframes } from "styled-components";
import { ReactComponent as ChevronDownIcon } from "../../assets/icons/chevron-down.svg";

/* Gentle panel breathe so the accordion card feels alive */
const panelFloat = keyframes`
  0%, 100% {
    filter: brightness(1);
  }
  50% {
    filter: brightness(1.02);
  }
`;

export const AccordionSection = styled.section`
  width: 100%;
  padding: 0 16px;
  gap: 16px;
  margin-bottom: 16px;
  &:last-child {
    margin-bottom: 0;
  }
`;

/* Water-glass panel that carries the FAQ — matches ModalContainer language */
export const AccordionCard = styled.div`
  position: relative;
  width: min(100%, 1040px);
  margin: 0 auto;
  border-radius: var(--radius-xl);
  border: 1px solid var(--glass-edge);
  background:
    radial-gradient(
      120% 60% at 30% -10%,
      rgba(var(--glass-white-rgb), 0.96) 0%,
      rgba(var(--glass-pale-rgb), 0.9) 55%,
      rgba(var(--glass-pale-rgb), 0.82) 100%
    ),
    linear-gradient(
      180deg,
      rgba(var(--glass-white-rgb), 0.94) 0%,
      rgba(var(--glass-pale-rgb), 0.88) 100%
    );
  backdrop-filter: blur(24px) saturate(180%);
  -webkit-backdrop-filter: blur(24px) saturate(180%);
  box-shadow:
    0 28px 56px -18px rgba(var(--color-brand-500-rgb), 0.36),
    0 10px 24px rgba(var(--color-brand-400-rgb), 0.18),
    inset 0 2px 0 rgba(255, 255, 255, 0.95),
    inset 0 -10px 20px rgba(var(--color-brand-200-rgb), 0.24),
    inset 0 0 0 1px rgba(255, 255, 255, 0.45);
  overflow: hidden;
  isolation: isolate;
  animation: ${panelFloat} 6s ease-in-out infinite;

  [data-theme="dark"] & {
    box-shadow:
      0 24px 48px -16px rgba(0, 0, 0, 0.55),
      0 8px 18px rgba(0, 0, 0, 0.32),
      inset 0 1px 0 rgba(255, 255, 255, 0.06),
      inset 0 -10px 20px rgba(var(--color-brand-700-rgb), 0.18);
    &::after {
      opacity: 0.3;
    }
  }

  /* Top specular sheen — real white so the highlight survives dark theme. */
  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 32px;
    right: 32px;
    height: 18px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.9) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(2px);
    pointer-events: none;
    z-index: 1;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  [data-theme="dark"] &::before {
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.18) 0%,
      rgba(255, 255, 255, 0) 100%
    );
  }

  /* Bottom aqua underglow */
  &::after {
    content: "";
    position: absolute;
    bottom: -8px;
    left: 20%;
    right: 20%;
    height: 14px;
    background: radial-gradient(
      ellipse at center top,
      rgba(var(--color-brand-400-rgb), 0.4) 0%,
      rgba(var(--color-brand-400-rgb), 0) 70%
    );
    pointer-events: none;
    z-index: -1;
  }
`;

export const ToggleButton = styled.button`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 22px 28px;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  transition: background 220ms var(--ease-snappy);

  &:hover {
    background: rgba(var(--color-brand-300-rgb), 0.14);
  }

  &:active {
    background: rgba(var(--color-brand-300-rgb), 0.22);
  }

  @media (max-width: 768px) {
    padding: 18px 20px 16px;
    gap: 12px;
  }
`;

/* Brand-gradient accordion title */
export const AccordionTitle = styled.h2`
  margin: 0;
  font-family: var(--font-family-primary);
  font-size: clamp(20px, 2vw, 26px);
  font-weight: 800;
  line-height: normal;
  letter-spacing: -0.025em;

  background: linear-gradient(
    0deg,
    var(--color-text-strong) 0%,
    var(--color-text-body) 45%,
    var(--color-brand-600) 100%
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  filter: drop-shadow(0 1px 2px rgba(var(--color-brand-600-rgb), 0.12));
`;

/* Frosted-glass chevron button wrapping the icon */
export const ToggleIcon = styled(ChevronDownIcon)`
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  padding: 8px;
  box-sizing: content-box;
  border-radius: 50%;
  border: 1px solid var(--glass-edge-soft);
  background: radial-gradient(
    130% 100% at 30% 15%,
    rgba(var(--glass-white-rgb), 0.72) 0%,
    rgba(var(--glass-white-rgb), 0.46) 45%,
    rgba(var(--glass-pale-rgb), 0.34) 100%
  );
  box-shadow:
    0 6px 14px rgba(var(--color-brand-500-rgb), 0.14),
    inset 0 1px 0 rgba(255, 255, 255, 0.95),
    inset 0 -3px 8px rgba(var(--color-brand-200-rgb), 0.22);
  transform: rotate(${(props) => (props.$open ? "180deg" : "0deg")});
  transition:
    transform 260ms var(--ease-snappy),
    border-color 200ms var(--ease-snappy),
    box-shadow 200ms var(--ease-snappy);

  path {
    stroke: var(--color-brand-600);
    transition: stroke 180ms var(--ease-snappy);
  }

  ${ToggleButton}:hover & {
    border-color: rgba(var(--color-brand-400-rgb), 0.5);
  }

  @media (max-width: 768px) {
    width: 18px;
    height: 18px;
    padding: 7px;
  }

  [data-theme="dark"] & {
    border-color: transparent;
    box-shadow:
      0 4px 10px rgba(0, 0, 0, 0.45),
      inset 0 -3px 8px rgba(var(--color-brand-700-rgb), 0.16);
  }

  [data-theme="dark"] & path {
    stroke: var(--color-text-strong);
  }
`;

export const AccordionContent = styled.div`
  display: grid;
  grid-template-rows: ${(props) => (props.$open ? "1fr" : "0fr")};
  opacity: ${(props) => (props.$open ? 1 : 0)};
  transition:
    grid-template-rows 280ms var(--ease-snappy),
    opacity 220ms var(--ease-snappy);
`;

export const AccordionContentInner = styled.div`
  overflow: hidden;
  padding: ${(props) => (props.$open ? "0 28px 28px" : "0 28px 0")};
  border-top: 1px solid
    ${(props) =>
      props.$open ? "rgba(var(--color-brand-300-rgb), 0.28)" : "transparent"};
  transition:
    padding 280ms var(--ease-snappy),
    border-color 220ms var(--ease-snappy);

  @media (max-width: 768px) {
    padding: ${(props) => (props.$open ? "0 20px 20px" : "0 20px 0")};
  }
`;

export const AccordionLead = styled.p`
  margin: 18px 0 0;
  color: var(--color-text-body);
  font-family: var(--font-family-primary);
  font-size: 16px;
  line-height: normal;
  letter-spacing: -0.005em;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

export const AccordionBlock = styled.div`
  margin-top: 22px;
`;

export const AccordionSubtitle = styled.h3`
  margin: 0;
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 16px;
  font-weight: 700;
  line-height: normal;
  letter-spacing: -0.015em;

  /* Small brand-gradient bullet accent on the left */
  display: inline-flex;
  align-items: center;
  gap: 10px;

  &::before {
    content: "";
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: linear-gradient(
      180deg,
      var(--color-brand-400) 0%,
      var(--color-brand-600) 100%
    );
    box-shadow:
      0 0 8px rgba(var(--color-brand-400-rgb), 0.45),
      inset 0 1px 0 rgba(255, 255, 255, 0.6);
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

export const AccordionText = styled.p`
  margin: 10px 0 0;
  color: var(--color-text-body);
  font-family: var(--font-family-primary);
  font-size: 15px;
  line-height: normal;
  letter-spacing: -0.005em;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

export const AccordionList = styled.ul`
  margin: 14px 0 0;
  padding: 0;
  list-style: none;
  color: var(--color-text-body);
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const AccordionListItem = styled.li`
  position: relative;
  padding-left: 26px;
  font-family: var(--font-family-primary);
  font-size: 15px;
  line-height: normal;
  letter-spacing: -0.005em;

  /* Water-drop bullet */
  &::before {
    content: "";
    position: absolute;
    left: 4px;
    top: 8px;
    width: 10px;
    height: 12px;
    background:
      radial-gradient(
          circle at 30% 38%,
          rgba(130, 205, 255, 1) 0%,
          rgba(var(--color-brand-200-rgb), 0) 60%
        )
        0 0 / 25% 20% no-repeat,
      radial-gradient(
        ellipse 115% 100% at 34% 28%,
        #ffffff 0%,
        var(--color-brand-200) 55%,
        var(--color-brand-600) 100%
      );
    -webkit-mask: url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Cpath d='M265.12 60.12a12 12 0 0 0-18.23 0C215.23 97.15 112 225.17 112 320c0 88.37 55.64 144 144 144s144-55.63 144-144c0-94.83-103.23-222.85-134.88-259.88'/%3E%3C/svg%3E")
      no-repeat center / contain;
    mask: url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Cpath d='M265.12 60.12a12 12 0 0 0-18.23 0C215.23 97.15 112 225.17 112 320c0 88.37 55.64 144 144 144s144-55.63 144-144c0-94.83-103.23-222.85-134.88-259.88'/%3E%3C/svg%3E")
      no-repeat center / contain;
    filter: drop-shadow(0 1px 2px rgba(var(--color-brand-600-rgb), 0.32));
  }

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;
